import { useGetApiV1SearchQuery, User } from '@wellesley/openapi'
import useFollowers from 'hooks/useFollowers'
import { useCurrentUser } from '@wellesley/user'
import InputFilter from '../InputFilter'
import useDebounceValue from 'hooks/useDebounceValue'
import { UseFormReturn } from 'react-hook-form'

const SEARCH_LIMIT = 50

type CmProps<TForm extends { search?: string }> = {
  users: Array<User | string>
  form: UseFormReturn<TForm>
  searchText: string
  setSearchText: (text: string) => void
  filter?: string[]
  placeholder?: string
  onSelect: (users: Array<User | string>) => void
  error?: string
}

const UserSelect = <TForm extends { search?: string }>({
  users,
  onSelect,
  filter,
  placeholder,
  searchText,
  setSearchText,
  form,
}: CmProps<TForm>) => {
  const { user } = useCurrentUser()
  const { followers, isLoading: isFollowersLoading } = useFollowers({ user })

  const debouncedSearchText = useDebounceValue(searchText, 300)

  const { data, isLoading: isSearchLoading } = useGetApiV1SearchQuery(
    {
      q: debouncedSearchText,
      limit: SEARCH_LIMIT,
      filter: ['users'],
    },
    { skip: !debouncedSearchText },
  )

  const isLoading = isFollowersLoading || isSearchLoading

  const filterAlreadyAdded = (list: User[]) =>
    list.filter((user) => !filter?.includes(user.id))

  const selectedList = filterAlreadyAdded(users as User[])
  const showPlaceholder = selectedList.length === 0

  const usersList = (debouncedSearchText ? data?.users : followers) || []

  return (
    <InputFilter
      form={form}
      onSelect={onSelect}
      selectedList={users}
      dropdownList={filterAlreadyAdded(usersList)}
      isLoading={isLoading}
      searchText={debouncedSearchText}
      setSearchText={setSearchText}
      placeholder={showPlaceholder ? placeholder : undefined}
      autoFocus={true}
      resetOnSelect={true}
      isStaticList={true}
      listMaxHeight={435}
      isUsersList={true}
      acceptEmail={true}
      listClassName="mt-xxs !p-0 border-none shadow-none static"
    />
  )
}

export default UserSelect
